import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getUserData, showSidebar } from "../store/login.slice";
import { getExamToReview, setReviewedExam } from "../store/test.slice";
import { useGardeTestMutation } from "../api/testSlice.ts/testSlice";
import useTokenSpoilCheck from "../hooks/tokenspoilcheck";
import Test, { ReviewModeData, ReviewModeItem } from "./test";
import styles from "./reviewTest.module.scss";

/*
 * Completed test review (/reviewTest, /reviewTest/:guid).
 * Loads the graded test, turns it into the same shape as a created test,
 * and shows the normal test page in review mode.
 */

function buildReview(reviewedTest, questionOrder, onExit): ReviewModeData {
  const test = reviewedTest?.test;
  const itemsById: Record<string, ReviewModeItem> = {};

  (test?.Grades ?? []).forEach((grade) => {
    itemsById[String(grade.QuestionId)] = {
      kind: "mc",
      grade,
      score: grade.Points * 100,
    };
  });
  (test?.PrescriptionGrades ?? []).forEach((grade) => {
    itemsById[String(grade.QuestionId)] = {
      kind: "rx",
      grade,
      score: grade.TotalPoints * 25, // TotalPoints is out of 4
    };
  });

  // Test order when we know it, otherwise multiple choice then prescriptions
  const knownIds = Object.keys(itemsById).map(Number);
  const order: number[] =
    questionOrder?.length > 0 ? questionOrder : knownIds;
  order.forEach((id) => {
    if (!itemsById[String(id)]) itemsById[String(id)] = { kind: "none" };
  });

  const multipleChoice = (test?.Grades ?? []).filter((grade) =>
    order.includes(grade.QuestionId),
  );

  return {
    itemsById,
    overallGrade:
      reviewedTest?.prevData?.GradeForTest ?? test?.TotalGrade ?? undefined,
    onExit,
    // Same shape the test page reads from the store
    createdExam: {
      guid: reviewedTest?.guid,
      tutorMode: false,
      QuestionsForTest: order,
      // Same answer choices the user saw, so the same options are shown
      AnswerChoicesForTest: multipleChoice.map((grade) => ({
        question_id: grade.QuestionId,
        answer_serials: grade.YourAnswersChoices ?? [],
      })),
      CapturedData: {
        answersById: Object.fromEntries(
          multipleChoice.map((grade) => [
            grade.QuestionId,
            grade.YourAnswersSerials ?? [],
          ]),
        ),
      },
    },
  };
}

function ReviewTest() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { guid } = useParams();
  const userData = useSelector(getUserData);
  const reviewedTest = useSelector(getExamToReview);
  const [errorStatusCode, setErrorStatusCode] = useState(undefined);
  useTokenSpoilCheck(errorStatusCode);

  const [gardeTest] = useGardeTestMutation();
  const reviewMatchesLink = !guid || reviewedTest?.guid == guid;

  // Opened from a link: grade/fetch the test here
  useEffect(() => {
    if (!guid || reviewedTest?.guid == guid) return;
    gardeTest({ testData: { guid } })
      .unwrap()
      .then((result) => {
        const prevData = result.CompletedTests?.find(
          (test) => test.guid == guid,
        );
        dispatch(
          setReviewedExam({
            examToReview: { test: result, guid, prevData: prevData ?? {} },
          }),
        );
      })
      .catch((error) => {
        toast(error?.data?.message ?? "Couldn't load this test");
        setErrorStatusCode(error?.status);
        navigate("/completeTests");
      });
  }, [guid]);

  // Nothing to review and nothing to load: back to the list
  useEffect(() => {
    if (!guid && reviewedTest == undefined) navigate("/completeTests");
  }, [guid, reviewedTest]);

  const questionOrder =
    reviewedTest?.prevData?.questions_for_test ??
    userData.CompletedTests?.find((test) => test.guid == reviewedTest?.guid)
      ?.questions_for_test;

  const review = useMemo(
    () =>
      reviewedTest && reviewMatchesLink
        ? buildReview(reviewedTest, questionOrder, () => {
            dispatch(setReviewedExam({ examToReview: undefined }));
            dispatch(showSidebar());
            navigate("/completeTests");
          })
        : undefined,
    [reviewedTest, questionOrder, reviewMatchesLink],
  );

  if (!review) {
    return <div className={styles.message}>Loading test review...</div>;
  }

  if (review.createdExam.QuestionsForTest.length === 0) {
    return (
      <div className={styles.message}>
        <span>This test has no graded questions to review.</span>
        <button className={styles.button} onClick={review.onExit}>
          Back to Complete Tests
        </button>
      </div>
    );
  }

  // key: a different test starts the page fresh
  return <Test key={reviewedTest.guid} review={review} />;
}

export default ReviewTest;
